import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Save, Palette, Mail, Flag, Plug, Lock, Building2, KeySquare, Calculator, FileText, GraduationCap, Plus, Trash2, IndianRupee, Cloud, CheckCircle2, XCircle, Loader2, AlertTriangle, RefreshCw, Target, Sparkles, Pencil, Briefcase, MapPin, AtSign, BarChart2, X, Scale, GripVertical, UserCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { applyTheme } from '../../theme/applyTheme';
import { useSettings } from '../../store/settings';
import { DEFAULT_PAYMENT_SETTINGS } from '../../utils/daewooFinance';

const TABS = [
    { id: 'general',        label: 'General',        icon: Building2 },
    { id: 'theme',          label: 'Brand & Theme',  icon: Palette },
    { id: 'approvals',      label: 'Approvals',      icon: KeySquare },
    { id: 'finance',        label: 'Finance',        icon: Calculator },
    { id: 'communications', label: 'Communications', icon: Mail },
    { id: 'email-templates', label: 'Email templates', icon: UserCheck },
    { id: 'features',       label: 'Feature Flags',  icon: Flag },
    { id: 'integrations',   label: 'Integrations',   icon: Plug },
    { id: 'security',       label: 'Security',       icon: Lock },
    { id: 'onboarding',     label: 'Onboarding Terms', icon: FileText },
    { id: 'training',       label: 'Training',       icon: GraduationCap },
    { id: 'incentive',      label: 'Incentive',      icon: IndianRupee },
    { id: 'kpi-targets',    label: 'KPI Targets',    icon: Target },
    { id: 'org-directory',  label: 'Org Directory',  icon: Briefcase },
    { id: 'legal',          label: 'Legal',          icon: Scale },
    { id: 'zoho',           label: 'Zoho CRM',       icon: Cloud },
    { id: 'kpi-report',    label: 'KPI Reports',    icon: BarChart2 },
];

export default function Settings() {
    const [tab, setTab] = useState('general');
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { api.get('/settings').then(r => setS(r.data)); }, []);

    const save = async (sectionPatch) => {
        setBusy(true);
        try {
            const { data } = await api.patch('/settings', sectionPatch);
            setS(data);
            if (sectionPatch.theme) applyTheme(mapThemeForApply(data.theme));
            toast.success('Saved');
            await useSettings.getState().load();
        } finally { setBusy(false); }
    };

    if (!s) return null;

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-extrabold">Settings</h1>
                <p className="text-sm text-ink-500">Centrally manage everything from here. Changes apply live.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
                {/* Tabs */}
                <div className="ag-card p-2 space-y-1 h-fit">
                    {TABS.map(t => (
                        <button key={t.id} onClick={() => setTab(t.id)}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium w-full transition
                                ${tab === t.id ? 'bg-brand-teal-50 text-brand-teal-700' : 'text-ink-700 hover:bg-ink-100'}`}>
                            <t.icon className="h-4 w-4" /> {t.label}
                        </button>
                    ))}
                    <div className="border-t border-ink-100 my-2" />
                    <Link to="/admin/permissions" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-ink-700 hover:bg-ink-100">
                        <KeySquare className="h-4 w-4" /> Per-user permissions →
                    </Link>
                </div>

                {/* Panels */}
                <div className="space-y-4">
                    {tab === 'general'        && <GeneralPanel        s={s} busy={busy} onSave={save} />}
                    {tab === 'theme'          && <ThemePanel          s={s} busy={busy} onSave={save} />}
                    {tab === 'approvals'      && <ApprovalsPanel      s={s} busy={busy} onSave={save} />}
                    {tab === 'finance'        && <FinancePanel        s={s} busy={busy} onSave={save} />}
                    {tab === 'communications' && <CommunicationsPanel s={s} busy={busy} onSave={save} />}
                    {tab === 'email-templates' && <EmailTemplatesPanel s={s} busy={busy} onSave={save} />}
                    {tab === 'features'       && <FeaturesPanel       s={s} busy={busy} onSave={save} />}
                    {tab === 'integrations'   && <IntegrationsPanel   s={s} busy={busy} onSave={save} />}
                    {tab === 'security'       && <SecurityPanel       s={s} busy={busy} onSave={save} />}
                    {tab === 'onboarding'     && <OnboardingTermsPanel />}
                    {tab === 'training'       && <TrainingPanel />}
                    {tab === 'incentive'      && <IncentivePanel s={s} busy={busy} onSave={save} />}
                    {tab === 'kpi-targets'    && <KpiTargetsPanel />}
                    {tab === 'org-directory'  && <OrgDirectoryPanel />}
                    {tab === 'legal'          && <LegalPanel s={s} busy={busy} onSave={save} />}
                    {tab === 'zoho'           && <ZohoPanel />}
                    {tab === 'kpi-report'    && <KpiReportPanel s={s} busy={busy} onSave={save} />}
                </div>
            </div>
        </div>
    );
}

/* ─── General ─── */
function GeneralPanel({ s, busy, onSave }) {
    const [f, setF] = useState({ appName: s.appName, tagline: s.tagline, contactEmail: s.contactEmail, supportPhone: s.supportPhone });
    return (
        <Section title="General">
            <Field label="App name" value={f.appName} onChange={v => setF({...f, appName: v})} />
            <Field label="Tagline"  value={f.tagline} onChange={v => setF({...f, tagline: v})} />
            <div className="grid grid-cols-2 gap-3">
                <Field label="Contact email" value={f.contactEmail} onChange={v => setF({...f, contactEmail: v})} />
                <Field label="Support phone" value={f.supportPhone} onChange={v => setF({...f, supportPhone: v})} />
            </div>
            <SaveBar busy={busy} onSave={() => onSave(f)} />
        </Section>
    );
}

/* ─── Theme (live preview) ─── */
function ThemePanel({ s, busy, onSave }) {
    const [t, setT] = useState({ ...s.theme });
    const tryLive = (next) => {
        setT(next);
        applyTheme(mapThemeForApply(next));
    };
    return (
        <>
            <Section title="Brand & Theme">
                <p className="text-xs text-ink-500 mb-3">Edits below preview live in this browser. Click <b>Save</b> to persist for everyone.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <ColorField label="Primary teal"  value={t.teal}      onChange={v => tryLive({...t, teal: v})} />
                    <ColorField label="Teal 700"      value={t.teal_700}  onChange={v => tryLive({...t, teal_700: v})} />
                    <ColorField label="Teal 50"       value={t.teal_50}   onChange={v => tryLive({...t, teal_50: v})} />
                    <ColorField label="Yellow"        value={t.yellow}    onChange={v => tryLive({...t, yellow: v})} />
                    <ColorField label="Red"           value={t.red}       onChange={v => tryLive({...t, red: v})} />
                    <ColorField label="Dark"          value={t.dark}      onChange={v => tryLive({...t, dark: v})} />
                    <ColorField label="Orange"        value={t.orange}    onChange={v => tryLive({...t, orange: v})} />
                    <ColorField label="Gold"          value={t.gold}      onChange={v => tryLive({...t, gold: v})} />
                    <ColorField label="Ivory"         value={t.ivory}     onChange={v => tryLive({...t, ivory: v})} />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                    <Select label="Density" value={t.density} options={['comfortable', 'compact']} onChange={v => tryLive({...t, density: v})} />
                    <Field  label="Card radius (px)" type="number" value={t.radius} onChange={v => tryLive({...t, radius: Number(v)})} />
                </div>

                <Preview />

                <SaveBar busy={busy} onSave={() => onSave({ theme: t })} />
            </Section>
        </>
    );
}

function Preview() {
    return (
        <div className="mt-5">
            <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-2">Live preview</div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
                {[
                    ['teal','bg-brand-teal text-white'], ['teal-50','bg-brand-teal-50 text-brand-teal-700'],
                    ['yellow','bg-brand-yellow text-brand-dark'], ['red','bg-brand-red text-white'],
                    ['dark','bg-brand-dark text-white'], ['ivory','bg-brand-ivory text-brand-dark'],
                ].map(([k, c]) => <span key={k} className={`ag-chip ${c} justify-center`}>{k}</span>)}
            </div>
            <div className="flex gap-2">
                <button type="button" className="ag-btn-primary">Primary</button>
                <button type="button" className="ag-btn-ghost">Ghost</button>
                <button type="button" className="ag-btn-danger">Destructive</button>
            </div>
        </div>
    );
}

/* ─── Approvals ─── */
function ApprovalsPanel({ s, busy, onSave }) {
    const [a, setA] = useState({ ...s.approvals });
    return (
        <Section title="Approvals">
            <Toggle  label="Auto-approve new sales signups" value={a.autoApprove}  onChange={v => setA({...a, autoApprove: v})} hint="If on, signups skip the approval queue and become active immediately. Off = manual review (recommended)." />
            <Toggle  label="Notify all admins on signup" value={a.notifyAdmins}    onChange={v => setA({...a, notifyAdmins: v})} />
            <Select  label="Default role on signup" value={a.defaultRole} options={['sales_user', 'manager', 'care_user']} onChange={v => setA({...a, defaultRole: v})} />
            <SaveBar busy={busy} onSave={() => onSave({ approvals: a })} />
        </Section>
    );
}

/* ─── Finance (client ledger + Daewoo-style payment rules) ─── */
function FinancePanel({ s, busy, onSave }) {
    const mergeFin = (fin) => ({
        defaultGstPercent: Number.isFinite(Number(fin?.defaultGstPercent)) ? Number(fin.defaultGstPercent) : 18,
        defaultTdsPercent: Number.isFinite(Number(fin?.defaultTdsPercent)) ? Number(fin.defaultTdsPercent) : 2,
        gstTdsRounding: fin?.gstTdsRounding === 'truncate2dp' ? 'truncate2dp' : 'round',
    });
    const mergePay = (ps) => ({ ...DEFAULT_PAYMENT_SETTINGS, ...(ps && typeof ps === 'object' ? ps : {}) });

    const [f, setF] = useState(() => mergeFin(s.finance));
    const [p, setP] = useState(() => mergePay(s.paymentSettings));
    useEffect(() => { setF(mergeFin(s.finance)); setP(mergePay(s.paymentSettings)); }, [s]);

    const saveFinance = async () => {
        const finance = {
            defaultGstPercent: Math.min(100, Math.max(0, Number(f.defaultGstPercent) || 0)),
            defaultTdsPercent: Math.min(100, Math.max(0, Number(f.defaultTdsPercent) || 0)),
            gstTdsRounding: f.gstTdsRounding,
        };
        const paymentSettings = {
            loiPercent: Math.min(100, Math.max(0, Number(p.loiPercent) || 0)),
            agreementPercent: Math.min(100, Math.max(0, Number(p.agreementPercent) ?? 100)),
            loiCalculationType: p.loiCalculationType === 'final_payable' ? 'final_payable' : 'deal_amount',
            agreementCalculationType: p.agreementCalculationType === 'final_payable' ? 'final_payable' : 'deal_amount',
            filterUsersByLocation: Boolean(p.filterUsersByLocation),
            franchiseAvailableThresholdPercent: Math.min(100, Math.max(0, Number(p.franchiseAvailableThresholdPercent) || 0)),
            franchiseBookedThresholdPercent: Math.min(100, Math.max(0, Number(p.franchiseBookedThresholdPercent) || 0)),
            paymentAgingWarningDays: Math.min(730, Math.max(1, Math.round(Number(p.paymentAgingWarningDays) || 45))),
            defaultDealType: p.defaultDealType === 'cash' ? 'cash' : 'online',
        };
        await onSave({ finance, paymentSettings });
    };

    return (
        <Section title="Finance">
            <p className="text-xs text-ink-500">
                Tax defaults apply when a submission has no explicit GST/TDS amounts. <b>Payment settings</b> drive LOI/agreement thresholds, default deal type, franchise KPI bands, and aging warnings — aligned with the Daewoo internal app. Dashboard and client ledger use these after Save.
            </p>
            <div className="text-[11px] font-bold uppercase tracking-wider text-ink-600 pt-1">Tax defaults</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Default GST %" type="number" value={f.defaultGstPercent} onChange={v => setF({ ...f, defaultGstPercent: v })} hint="0–100. Typical slab on services: 18%." />
                <Field label="Default TDS %" type="number" value={f.defaultTdsPercent} onChange={v => setF({ ...f, defaultTdsPercent: v })} hint="0–100 when row has no TDS rate." />
            </div>
            <Select label="GST/TDS amount rounding" value={f.gstTdsRounding} options={['round', 'truncate2dp']} onChange={v => setF({ ...f, gstTdsRounding: v })} />

            <div className="text-[11px] font-bold uppercase tracking-wider text-ink-600 pt-3 border-t border-ink-100">Payment &amp; LOI (Daewoo-style)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="LOI threshold %" type="number" value={p.loiPercent} onChange={v => setP({ ...p, loiPercent: v })} hint="Percent of LOI base required before LOI stage." />
                <Field label="Agreement threshold %" type="number" value={p.agreementPercent} onChange={v => setP({ ...p, agreementPercent: v })} hint="Percent of agreement base required before agreement stage (typically 100)." />
                <Select label="Default deal type (no dealType on form)" value={p.defaultDealType} options={['online', 'cash']} onChange={v => setP({ ...p, defaultDealType: v })} />
                <Select label="LOI base" value={p.loiCalculationType} options={['deal_amount', 'final_payable']} onChange={v => setP({ ...p, loiCalculationType: v })} />
                <Select label="Agreement base" value={p.agreementCalculationType} options={['deal_amount', 'final_payable']} onChange={v => setP({ ...p, agreementCalculationType: v })} />
            </div>
            <Toggle label="Filter users by location (CRM parity)" value={p.filterUsersByLocation} onChange={v => setP({ ...p, filterUsersByLocation: v })} hint="Reserved for territory views; stored for future modules." />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Franchise “available” threshold %" type="number" value={p.franchiseAvailableThresholdPercent} onChange={v => setP({ ...p, franchiseAvailableThresholdPercent: v })} />
                <Field label="Franchise “booked” threshold %" type="number" value={p.franchiseBookedThresholdPercent} onChange={v => setP({ ...p, franchiseBookedThresholdPercent: v })} />
                <Field label="Payment aging warning (days)" type="number" value={p.paymentAgingWarningDays} onChange={v => setP({ ...p, paymentAgingWarningDays: v })} hint="Days after token for aging KPIs." />
            </div>
            <SaveBar busy={busy} onSave={saveFinance} />
        </Section>
    );
}

/* ─── Email templates (onboarding, payment, refund × role) ─── */
const EMAIL_EVENTS = [
    { id: 'client_onboarding', label: 'Client onboarding', hint: 'Sent when sales registers a new client.' },
    { id: 'payment', label: 'Payment', hint: 'Sent when finance verifies a payment.' },
    { id: 'refund', label: 'Refund', hint: 'Sent when a refund is requested, approved, or rejected.' },
];
const EMAIL_AUDIENCES = [
    { id: 'client', label: 'Client' },
    { id: 'admin', label: 'Admin' },
    { id: 'sales', label: 'Sales users' },
    { id: 'user', label: 'User (manager)' },
    { id: 'finance', label: 'Finance' },
];
const EMAIL_VARS = '{{clientName}}, {{clientEmail}}, {{paymentAmount}}, {{paymentDate}}, {{refundAmount}}, {{refundStatus}}, {{refundReason}}, {{actorName}}, {{submittedBy}}, {{appName}}, {{supportEmail}}';

function EmailTemplatesPanel({ s, busy, onSave }) {
    const [eventKey, setEventKey] = useState('client_onboarding');
    const [audience, setAudience] = useState('client');
    const [events, setEvents] = useState(() => s.emailTemplates?.events || {});

    useEffect(() => {
        if (s.emailTemplates?.events) setEvents(s.emailTemplates.events);
    }, [s]);

    const ev = events[eventKey] || { enabled: true, templates: {} };
    const slot = ev.templates?.[audience] || { enabled: true, subject: '', bodyHtml: '' };

    const patchSlot = (patch) => {
        setEvents(prev => ({
            ...prev,
            [eventKey]: {
                ...prev[eventKey],
                templates: {
                    ...(prev[eventKey]?.templates || {}),
                    [audience]: { ...slot, ...patch },
                },
            },
        }));
    };

    const patchEvent = (patch) => {
        setEvents(prev => ({ ...prev, [eventKey]: { ...prev[eventKey], ...patch } }));
    };

    return (
        <>
            <Section title="Email templates">
                <p className="text-xs text-ink-500">
                    Separate templates per event and recipient role. Requires SMTP in <code>backend/.env</code>.
                    Placeholders: {EMAIL_VARS}
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                    {EMAIL_EVENTS.map(e => (
                        <button
                            key={e.id}
                            type="button"
                            onClick={() => { setEventKey(e.id); setAudience('client'); }}
                            className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ring-1 ${
                                eventKey === e.id ? 'bg-brand-teal text-white ring-brand-teal' : 'bg-ink-100 text-ink-700 ring-ink-200'
                            }`}
                        >
                            {e.label}
                        </button>
                    ))}
                </div>
                <p className="text-[11px] text-ink-500">{EMAIL_EVENTS.find(e => e.id === eventKey)?.hint}</p>
                <Toggle label="Send emails for this event" value={ev.enabled !== false} onChange={v => patchEvent({ enabled: v })} />
            </Section>

            <Section title="Recipient role">
                <div className="flex flex-wrap gap-2">
                    {EMAIL_AUDIENCES.map(a => (
                        <button
                            key={a.id}
                            type="button"
                            onClick={() => setAudience(a.id)}
                            className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ring-1 ${
                                audience === a.id ? 'bg-brand-teal-50 text-brand-teal-800 ring-brand-teal/30' : 'bg-white text-ink-600 ring-ink-200'
                            }`}
                        >
                            {a.label}
                        </button>
                    ))}
                </div>
                <Toggle label={`Send to ${EMAIL_AUDIENCES.find(a => a.id === audience)?.label}`} value={slot.enabled !== false} onChange={v => patchSlot({ enabled: v })} />
                <Field label="Subject" value={slot.subject || ''} onChange={v => patchSlot({ subject: v })} />
                <textarea
                    className="ag-input min-h-[200px] font-mono text-xs"
                    value={slot.bodyHtml || ''}
                    onChange={e => patchSlot({ bodyHtml: e.target.value })}
                />
                <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold mb-1">Preview</div>
                <div className="ag-onboard-html ag-card p-4 max-h-52 overflow-y-auto" dangerouslySetInnerHTML={{ __html: slot.bodyHtml || '<p class="text-ink-400">(empty)</p>' }} />
            </Section>

            {eventKey === 'client_onboarding' && audience === 'client' && (
                <>
                    <Section title="Client email — refund policy (appended)">
                        <textarea className="ag-input min-h-[140px] font-mono text-xs" value={ev.refundPolicyHtml || ''} onChange={e => patchEvent({ refundPolicyHtml: e.target.value })} />
                    </Section>
                    <Section title="Client email — terms &amp; conditions (appended)">
                        <textarea className="ag-input min-h-[140px] font-mono text-xs" value={ev.termsHtml || ''} onChange={e => patchEvent({ termsHtml: e.target.value })} />
                    </Section>
                </>
            )}

            <SaveBar
                busy={busy}
                onSave={() => onSave({ emailTemplates: { events } })}
            />
        </>
    );
}

/* ─── Communications ─── */
function CommunicationsPanel({ s, busy, onSave }) {
    const [c, setC] = useState({ ...s.communications });
    return (
        <Section title="Communications">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Toggle label="Email (SMTP) enabled" value={c.smtpEnabled}  onChange={v => setC({...c, smtpEnabled: v})} />
                <Toggle label="WhatsApp enabled"     value={c.whatsappEnabled} onChange={v => setC({...c, whatsappEnabled: v})} />
                <Toggle label="SMS enabled"          value={c.smsEnabled}      onChange={v => setC({...c, smsEnabled: v})} />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <Field label="SMTP host" value={c.smtpHost} onChange={v => setC({...c, smtpHost: v})} />
                <Field label="SMTP port" type="number" value={c.smtpPort} onChange={v => setC({...c, smtpPort: Number(v)})} />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <Toggle label="SMTP secure (TLS)" value={c.smtpSecure} onChange={v => setC({...c, smtpSecure: v})} />
                <Field  label="SMTP user" value={c.smtpUser} onChange={v => setC({...c, smtpUser: v})} />
            </div>
            <Field label="Mail from header" value={c.mailFrom} onChange={v => setC({...c, mailFrom: v})} hint='e.g. "AGEON Care &lt;care@ageon.in&gt;"' />
            <SaveBar busy={busy} onSave={() => onSave({ communications: c })} />
        </Section>
    );
}

/* ─── Feature flags ─── */
function FeaturesPanel({ s, busy, onSave }) {
    const [f, setF] = useState({ landingPage: true, ...s.features });
    return (
        <Section title="Feature flags">
            <p className="text-xs text-ink-500 mb-3">
                Turn modules on or off across the app. When <strong>Landing page</strong> is on, all signed-in users see a Landing page link in their profile menu and can open the marketing site at /.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(f).map(([k, v]) => (
                    <Toggle key={k} label={k.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase())} value={v} onChange={n => setF({ ...f, [k]: n })} />
                ))}
            </div>
            <SaveBar busy={busy} onSave={() => onSave({ features: f })} />
        </Section>
    );
}

/* ─── Integrations ─── */
function IntegrationsPanel({ s, busy, onSave }) {
    const [i, setI] = useState({ ...s.integrations });
    return (
        <Section title="Integrations">
            <Field label="Google Maps API key" value={i.googleMapsApiKey} onChange={v => setI({...i, googleMapsApiKey: v})} hint="Used by clinics locator (lands in Step 4)" />
            <Field label="Analytics key"        value={i.analyticsKey}     onChange={v => setI({...i, analyticsKey: v})} />
            <Toggle label="Zoho mock mode (don't actually call Zoho)" value={i.zohoMockMode} onChange={v => setI({...i, zohoMockMode: v})} />
            <Select label="Payment gateway" value={i.paymentGateway} options={['none', 'razorpay', 'stripe', 'cashfree']} onChange={v => setI({...i, paymentGateway: v})} />
            <SaveBar busy={busy} onSave={() => onSave({ integrations: i })} />
        </Section>
    );
}

/* ─── Security ─── */
function SecurityPanel({ s, busy, onSave }) {
    const [sec, setSec] = useState({ ...s.security, ipAllowlistText: (s.security.ipAllowlist || []).join('\n') });
    return (
        <Section title="Security">
            <div className="grid grid-cols-2 gap-3">
                <Field label="Password min length" type="number" value={sec.passwordMinLength} onChange={v => setSec({...sec, passwordMinLength: Number(v)})} />
                <Field label="Session days" type="number" value={sec.sessionDays} onChange={v => setSec({...sec, sessionDays: Number(v)})} />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <Toggle label="Require 2FA" value={sec.require2FA} onChange={v => setSec({...sec, require2FA: v})} />
                <Field  label="Audit retention (days)" type="number" value={sec.auditRetentionDays} onChange={v => setSec({...sec, auditRetentionDays: Number(v)})} />
            </div>
            <div>
                <label className="ag-label">IP allowlist (one per line; empty = allow all)</label>
                <textarea className="ag-input min-h-[110px] font-mono text-xs" value={sec.ipAllowlistText} onChange={e => setSec({...sec, ipAllowlistText: e.target.value})} />
            </div>
            <SaveBar busy={busy} onSave={() => onSave({ security: {
                passwordMinLength: sec.passwordMinLength,
                sessionDays: sec.sessionDays,
                require2FA: sec.require2FA,
                auditRetentionDays: sec.auditRetentionDays,
                ipAllowlist: sec.ipAllowlistText.split(/\n+/).map(s => s.trim()).filter(Boolean),
            }})} />
        </Section>
    );
}

/* ─── Zoho CRM (G1) ───
 * Drives the admin Test / Save flow. Credentials are stored on the
 * AppSetting.zoho block; secrets come back to the browser redacted
 * (•••••••• with prefix/suffix) — typing a fresh value in the field
 * overwrites; leaving the masked value alone keeps the stored secret.
 */
function ZohoPanel() {
    const [cfg, setCfg]         = useState(null);
    const [setMap, setSetMap]   = useState({});      // _set flags from server (clientSecret/refreshToken/webhookSecret)
    const [saving, setSaving]   = useState(false);
    const [testing, setTesting] = useState(false);
    const [pushing, setPushing] = useState(false);
    const [syncing, setSyncing] = useState(false);

    const load = () => api.get('/zoho/status').then(r => {
        setCfg(r.data.zoho || {});
        setSetMap(r.data.zoho?._set || {});
    });
    useEffect(() => { load(); }, []);

    if (!cfg) return <Section title="Zoho CRM"><p className="text-sm text-ink-500">Loading…</p></Section>;

    const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

    /* Strip the masked secrets if admin didn't change them. The backend
       already ignores incoming values that contain • but we send clean
       payloads to make the wire log readable. */
    const isMasked = (v) => typeof v === 'string' && v.includes('•');
    const buildPayload = () => {
        const out = { ...cfg };
        for (const k of ['clientSecret', 'refreshToken', 'webhookSecret']) {
            if (isMasked(out[k])) delete out[k];
        }
        delete out._set;
        return out;
    };

    const saveCfg = async () => {
        setSaving(true);
        try {
            await api.patch('/zoho/config', buildPayload());
            toast.success('Zoho settings saved');
            await load();
        } finally { setSaving(false); }
    };

    const testConnection = async () => {
        setTesting(true);
        try {
            const { data } = await api.post('/zoho/test-connection');
            if (data.mockMode) {
                toast.success('Mock mode — config saved, not contacted Zoho');
            } else {
                /* Long-lived toast so admin can read the org + module info. */
                toast.success(
                    data.moduleReadable
                        ? `Connected to "${data.orgName}" · module "${data.module}" (${data.fieldCount} fields)`
                        : `Connected to "${data.orgName}" — but module "${data.module}" is not readable. ${data.moduleReadError || 'Check token scopes.'}`,
                    { duration: 7000 }
                );
            }
            await load();
        } catch (err) {
            /* Backend now returns { ok:false, step, error, hint, raw }. The
               api interceptor toasted the bare error already — show the
               actionable hint as a second toast if there is one. */
            const hint = err?.response?.data?.hint;
            if (hint) {
                toast(hint, { duration: 10_000, style: { whiteSpace: 'pre-line', maxWidth: 460 } });
            }
            await load();
        }
        finally { setTesting(false); }
    };

    const testPush = async () => {
        setPushing(true);
        try {
            const { data } = await api.post('/zoho/test-push');
            toast.success(data.mockMode
                ? 'Mock push recorded — no Zoho call'
                : `Pushed to Zoho · ID ${data.zohoId}`);
            await load();
        } catch {/* toasted */}
        finally { setPushing(false); }
    };

    const syncLeads = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post('/zoho/sync-leads', null, { timeout: 300_000 });
            if (data.ok) {
                toast.success(`Synced ${data.upserted?.toLocaleString?.() ?? data.upserted} leads · ${data.dbCount?.toLocaleString?.() ?? data.dbCount} in database`, { duration: 6000 });
            } else {
                toast.error(data.error || 'Sync failed');
            }
            await load();
        } catch (err) {
            const msg = err?.response?.data?.error || err.message;
            if (/timeout/i.test(msg)) toast.error('Sync timed out — it may still be running; check sync status in a minute.');
        } finally { setSyncing(false); }
    };

    const lastTested = cfg.lastTestedAt ? new Date(cfg.lastTestedAt) : null;
    const leadSync = cfg.leadSync || {};

    return (
        <>
            <Section title={<>Zoho CRM <span className="ml-2 text-xs font-normal text-ink-500">{cfg.enabled ? 'enabled' : 'disabled'}{cfg.mockMode ? ' · mock' : ''}</span></>}>
                <p className="text-xs text-ink-500">
                    Paste your Zoho self-client credentials below, save, and click <b>Test connection</b>.
                    Region matters — pick the API/accounts domain that matches the Zoho data center your org sits in
                    (<code>.in</code> for India, <code>.com</code> for the US, <code>.eu</code> for Europe). Sensitive
                    fields are stored on the server; the UI only ever sees a masked copy.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <Toggle label="Enabled" value={!!cfg.enabled} onChange={v => set('enabled', v)} hint="Master switch. When off, the rest of the app treats Zoho push as a no-op." />
                    <Toggle label="Mock mode (don't contact Zoho)" value={!!cfg.mockMode} onChange={v => set('mockMode', v)} hint="Logs intended pushes locally; never makes a network call. Use until your prod credentials are verified." />
                </div>

                <div className="text-[11px] font-bold uppercase tracking-wider text-ink-600 pt-2">Fast track — paste a working access token</div>
                <p className="text-[11px] text-ink-500">
                    If you already minted an access token in Postman, paste it here. Test calls will use it directly
                    and skip the refresh-token grant entirely. Zoho access tokens expire in ~60 minutes — set the
                    expiry below or leave blank to default to +55 minutes from save time.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <SecretField
                        label="Access token (optional)"
                        value={cfg.accessToken}
                        hasStored={setMap.accessToken}
                        onChange={v => set('accessToken', v)}
                        hint="Paste a fresh `1000.abcd…` access token to skip the refresh flow."
                    />
                    <Field
                        label="Access token expires at"
                        type="datetime-local"
                        value={cfg.accessTokenExpiresAt ? new Date(cfg.accessTokenExpiresAt).toISOString().slice(0, 16) : ''}
                        onChange={v => set('accessTokenExpiresAt', v ? new Date(v).toISOString() : '')}
                        hint="Leave blank → defaults to 55 min from save."
                    />
                </div>

                <div className="text-[11px] font-bold uppercase tracking-wider text-ink-600 pt-2">OAuth credentials (refresh-token grant)</div>
                <p className="text-[11px] text-ink-500">Required if you want auto-refresh after the access token above expires.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Client ID" value={cfg.clientId} onChange={v => set('clientId', v)} placeholder="1000.XXXXXXXXXXXX" />
                    <SecretField label="Client secret" value={cfg.clientSecret} hasStored={setMap.clientSecret} onChange={v => set('clientSecret', v)} />
                </div>
                <SecretField label="Refresh token" value={cfg.refreshToken} hasStored={setMap.refreshToken} onChange={v => set('refreshToken', v)} hint="Long-lived token from the Self-Client → Generate Code flow." />

                <div className="text-[11px] font-bold uppercase tracking-wider text-ink-600 pt-2">Endpoints &amp; defaults</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Select label="API domain"      value={cfg.apiDomain}     options={['https://www.zohoapis.in', 'https://www.zohoapis.com', 'https://www.zohoapis.eu']} onChange={v => set('apiDomain', v)} />
                    <Select label="Accounts domain" value={cfg.accountsDomain} options={['https://accounts.zoho.in', 'https://accounts.zoho.com', 'https://accounts.zoho.eu']} onChange={v => set('accountsDomain', v)} />
                    <Field  label="Lead module"     value={cfg.leadModule}     onChange={v => set('leadModule', v)} hint="Default 'Leads'. Some orgs map this to 'Contacts' or a custom module." />
                    <Field  label="Default owner ID" value={cfg.defaultOwnerId} onChange={v => set('defaultOwnerId', v)} hint="Zoho user ID who receives leads whose AGEON BDM has no Zoho mapping." />
                </div>
                <SecretField label="Webhook secret" value={cfg.webhookSecret} hasStored={setMap.webhookSecret} onChange={v => set('webhookSecret', v)} hint="Shared secret for verifying incoming webhook payloads. Webhook URL: POST /api/zoho/webhook" />

                <div className="text-[11px] font-bold uppercase tracking-wider text-ink-600 pt-2">Local lead mirror (fast KPI)</div>
                <p className="text-[11px] text-ink-500">
                    Leads are copied into MongoDB when synced. KPI analytics reads from the database (instant)
                    instead of paginating Zoho on every page load. Run a full sync once — then the server
                    auto-syncs every 2 minutes (only changed leads) and webhooks update in real time.
                </p>
                {leadSync.lastSyncAt && (
                    <p className="text-xs text-ink-600">
                        Last sync: {new Date(leadSync.lastSyncAt).toLocaleString('en-IN')}
                        {leadSync.lastSyncOk ? '' : ' · failed'}
                        {leadSync.dbCount != null ? ` · ${Number(leadSync.dbCount).toLocaleString()} leads in DB` : ''}
                    </p>
                )}
                <div className="flex gap-2 pt-1 flex-wrap">
                    <button type="button" className="ag-btn-secondary text-sm" disabled={syncing || !cfg.enabled} onClick={syncLeads}>
                        {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        {syncing ? 'Syncing all leads…' : 'Sync all leads to database'}
                    </button>
                </div>

                <div className="flex justify-end pt-2 gap-2">
                    <button className="ag-btn-primary" disabled={saving} onClick={saveCfg}>
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {saving ? 'Saving…' : 'Save settings'}
                    </button>
                </div>
            </Section>

            <Section title="Verify">
                <p className="text-xs text-ink-500">
                    <b>Test connection</b> mints an access token via the refresh-token grant and pulls module metadata —
                    fastest proof the credentials work. <b>Test push</b> creates a sample lead so you can see it land
                    in Zoho's Leads list. Both actions stamp the last-tested timestamp below.
                </p>

                <div className="flex gap-2 pt-2 flex-wrap">
                    <button className="ag-btn-outline" disabled={testing || saving} onClick={testConnection}>
                        {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
                        {testing ? 'Testing…' : 'Test connection'}
                    </button>
                    <button className="ag-btn-outline" disabled={pushing || saving || !cfg.enabled} onClick={testPush}>
                        {pushing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cloud className="h-4 w-4" />}
                        {pushing ? 'Pushing…' : 'Test push (sample lead)'}
                    </button>
                </div>

                {lastTested && (
                    <div className={`mt-3 rounded-lg p-3 text-sm inline-flex items-start gap-2 ring-1
                        ${cfg.lastTestedOk
                            ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
                            : 'bg-rose-50    text-rose-800    ring-rose-200'}`}>
                        {cfg.lastTestedOk
                            ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                            : <XCircle      className="h-4 w-4 mt-0.5 shrink-0" />}
                        <div>
                            <b>Last tested {lastTested.toLocaleString('en-IN')}</b>
                            <div className="text-[12px] mt-0.5">{cfg.lastTestedNote || '—'}</div>
                        </div>
                    </div>
                )}

                {!cfg.enabled && (
                    <div className="mt-3 rounded-lg p-3 text-[11px] bg-amber-50 text-amber-800 ring-1 ring-amber-200 inline-flex items-start gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        Test push is disabled until you turn on <b>Enabled</b> above and save. Use the toggle to flip it on
                        once your credentials are verified.
                    </div>
                )}
            </Section>
        </>
    );
}

/* Masked-input variant for secret fields. Backend returns the stored
   value masked (e.g. "abcd••••••wxyz") and a `_set` flag; we let admin
   either keep the masked value (no change) or type a fresh value to
   overwrite. */
function SecretField({ label, value, hasStored, onChange, hint }) {
    return (
        <div>
            <label className="ag-label">
                {label}
                {hasStored && <span className="ml-2 text-[10px] text-emerald-700 font-bold uppercase tracking-wider">set</span>}
            </label>
            <input
                className="ag-input font-mono text-xs"
                value={value ?? ''}
                onChange={e => onChange(e.target.value)}
                placeholder={hasStored ? 'leave masked to keep current' : 'paste secret'}
                autoComplete="off"
                spellCheck={false}
            />
            {hint && <div className="text-[11px] text-ink-500 mt-1">{hint}</div>}
        </div>
    );
}

/* ─── Incentive defaults (Chunk E2) ───
 * Default pool % + base, used when admin clicks "Confirm & set pool" on a
 * fully-paid client. Admin can still override the percent per-client. */
function IncentivePanel({ s, busy, onSave }) {
    const i = s.incentive || { defaultPoolPercent: 2, poolBase: 'deal_amount', opsMinPercent: 0 };
    const [f, setF] = useState({
        defaultPoolPercent: Number(i.defaultPoolPercent ?? 2),
        poolBase:           i.poolBase || 'deal_amount',
        opsMinPercent:      Number(i.opsMinPercent ?? 0),
    });
    return (
        <Section title="Incentive defaults">
            <p className="text-xs text-ink-500">
                Drives the deal-closure pool. When a client's payments hit 100%, the row appears under
                Incentives → Deal closures → Pending confirmation. Admin can override the pool % per-client
                at confirmation time.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                    label="Default pool %"
                    type="number"
                    value={f.defaultPoolPercent}
                    onChange={v => setF({ ...f, defaultPoolPercent: Math.max(0, Math.min(100, Number(v) || 0)) })}
                    hint="0–100. Default is 2 (= 2% of the chosen base). On a ₹1 cr deal that's ₹2 L."
                />
                <Select
                    label="Pool base"
                    value={f.poolBase}
                    options={['deal_amount', 'final_payable']}
                    onChange={v => setF({ ...f, poolBase: v })}
                />
            </div>
            <Field
                label="Ops floor %"
                type="number"
                value={f.opsMinPercent}
                onChange={v => setF({ ...f, opsMinPercent: Math.max(0, Math.min(100, Number(v) || 0)) })}
                hint="Minimum % of the pool reserved for Operations. Blocks the BDM from committing a sales split larger than (pool − floor). 0 disables the floor."
            />
            <SaveBar busy={busy} onSave={() => onSave({ incentive: f })} />
        </Section>
    );
}

/* ─── Onboarding Terms (KRA / KPI editor) ───
 * Hits /api/onboarding-content (GET + PATCH /kra + PATCH /kpi). Saving any
 * body bumps its version, which marks every prior acceptance "stale". A
 * small live preview pane mirrors the HTML so admins can sanity-check
 * before saving. */
/* F6: Role tabs. "global" is the fallback used when a role-specific slot
   is empty. The keys mirror the backend's ROLE_KEYS constant. */
const KRA_TABS = [
    { id: 'global',     label: 'Default (fallback)' },
    { id: 'bda',        label: 'BDA' },
    { id: 'bde',        label: 'BDE' },
    { id: 'bdm',        label: 'BDM' },
    { id: 'operations', label: 'Operations' },
    { id: 'finance',    label: 'Finance' },
    { id: 'sub_admin',  label: 'Sub-admin' },
];

function OnboardingTermsPanel() {
    const [doc, setDoc] = useState(null);
    const [tab, setTab] = useState('global');
    const [kraHtml, setKra] = useState('');
    const [kpiHtml, setKpi] = useState('');
    const [busyKra, setBusyKra] = useState(false);
    const [busyKpi, setBusyKpi] = useState(false);

    /* Pull the admin view (every slot) so we can swap between tabs without
       refetching. */
    const reload = () => api.get('/onboarding-content/admin').then(r => setDoc(r.data));
    useEffect(() => { reload(); }, []);

    /* Refresh the textarea buffers when the active tab changes. */
    useEffect(() => {
        if (!doc) return;
        if (tab === 'global') {
            setKra(doc.global?.kraHtml || '');
            setKpi(doc.global?.kpiHtml || '');
        } else {
            const slot = doc.roles?.[tab] || {};
            setKra(slot.kraHtml || '');
            setKpi(slot.kpiHtml || '');
        }
    }, [tab, doc]);

    if (!doc) return <Section title="Onboarding Terms"><p className="text-sm text-ink-500">Loading…</p></Section>;

    /* Routes differ for global vs role slots. */
    const routeKra = tab === 'global' ? '/onboarding-content/kra'
                                       : `/onboarding-content/roles/${tab}/kra`;
    const routeKpi = tab === 'global' ? '/onboarding-content/kpi'
                                       : `/onboarding-content/roles/${tab}/kpi`;

    /* For role tabs we allow empty body (clears the override → falls back
       to global). For the global tab we require non-empty. */
    const saveKra = async () => {
        if (tab === 'global' && !kraHtml.trim()) return toast.error('Global KRA cannot be empty');
        setBusyKra(true);
        try {
            await api.patch(routeKra, { kraHtml });
            await reload();
            toast.success(kraHtml.trim()
                ? `KRA saved for ${labelFor(tab)}`
                : `${labelFor(tab)} KRA cleared — falls back to global`);
        } finally { setBusyKra(false); }
    };
    const saveKpi = async () => {
        if (tab === 'global' && !kpiHtml.trim()) return toast.error('Global KPI cannot be empty');
        setBusyKpi(true);
        try {
            await api.patch(routeKpi, { kpiHtml });
            await reload();
            toast.success(kpiHtml.trim()
                ? `KPI saved for ${labelFor(tab)}`
                : `${labelFor(tab)} KPI cleared — falls back to global`);
        } finally { setBusyKpi(false); }
    };

    /* Version pill data — global or the active role slot. */
    const sliceVer = tab === 'global'
        ? { kraVersion: doc.global?.kraVersion, kraUpdatedAt: doc.global?.kraUpdatedAt,
            kpiVersion: doc.global?.kpiVersion, kpiUpdatedAt: doc.global?.kpiUpdatedAt }
        : { kraVersion: doc.roles?.[tab]?.kraVersion || 0, kraUpdatedAt: doc.roles?.[tab]?.kraUpdatedAt,
            kpiVersion: doc.roles?.[tab]?.kpiVersion || 0, kpiUpdatedAt: doc.roles?.[tab]?.kpiUpdatedAt };

    const slotEmpty = tab !== 'global' && !(doc.roles?.[tab]?.kraHtml || doc.roles?.[tab]?.kpiHtml);

    return (
        <>
            <Section title="Onboarding Terms">
                <p className="text-xs text-ink-500">
                    Each role can have its own KRA &amp; KPI. Users are matched on
                    <code> salesDesignation</code> (BDA / BDE / BDM / Operations) first,
                    then <code>role</code> (Finance / Sub-admin). Anyone outside those slots
                    sees the <b>Default</b> fallback. Save bumps the version — users who
                    already accepted an older version of <i>their</i> slot will re-accept.
                </p>
                <div className="flex gap-1.5 flex-wrap pt-2">
                    {KRA_TABS.map(t => {
                        const empty = t.id !== 'global' && !(doc.roles?.[t.id]?.kraHtml || doc.roles?.[t.id]?.kpiHtml);
                        return (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                className={`text-xs px-2.5 py-1 rounded-full transition
                                    ${tab === t.id ? 'bg-brand-teal text-white'
                                                   : empty ? 'bg-ink-100 text-ink-500 italic'
                                                           : 'bg-ink-100 text-ink-700 hover:bg-ink-200'}`}
                            >
                                {t.label}{empty && ' · uses default'}
                            </button>
                        );
                    })}
                </div>
                {slotEmpty && (
                    <div className="text-[11px] text-ink-500 mt-2 inline-flex items-center gap-1">
                        <FileText className="h-3 w-3" /> This slot is empty — users with this role currently see the Default KRA &amp; KPI.
                        Type below and save to override.
                    </div>
                )}
            </Section>

            <Section title={<>KRA · {labelFor(tab)} <VersionPill v={sliceVer.kraVersion} updatedAt={sliceVer.kraUpdatedAt} /></>}>
                <textarea
                    className="ag-input min-h-[220px] font-mono text-xs whitespace-pre"
                    value={kraHtml}
                    onChange={e => setKra(e.target.value)}
                    placeholder={tab === 'global' ? '' : 'Leave empty to fall back to the Default KRA.'}
                />
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold mb-1">Live preview</div>
                    <div className="ag-onboard-html ag-card p-4 max-h-60 overflow-y-auto"
                         dangerouslySetInnerHTML={{ __html: kraHtml || '<p style="color:var(--ink-500)">(empty — falls back to Default)</p>' }} />
                </div>
                <SaveBar busy={busyKra} onSave={saveKra} />
            </Section>

            <Section title={<>KPI · {labelFor(tab)} <VersionPill v={sliceVer.kpiVersion} updatedAt={sliceVer.kpiUpdatedAt} /></>}>
                <textarea
                    className="ag-input min-h-[220px] font-mono text-xs whitespace-pre"
                    value={kpiHtml}
                    onChange={e => setKpi(e.target.value)}
                    placeholder={tab === 'global' ? '' : 'Leave empty to fall back to the Default KPI.'}
                />
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold mb-1">Live preview</div>
                    <div className="ag-onboard-html ag-card p-4 max-h-60 overflow-y-auto"
                         dangerouslySetInnerHTML={{ __html: kpiHtml || '<p style="color:var(--ink-500)">(empty — falls back to Default)</p>' }} />
                </div>
                <SaveBar busy={busyKpi} onSave={saveKpi} />
            </Section>
        </>
    );
}
function labelFor(id) {
    return KRA_TABS.find(t => t.id === id)?.label || id;
}
/* ─── Training (videos + quiz editor) ───
 * GETs /api/training-content (admin sees full doc incl. correctIndex),
 * PATCHes back the whole shape. Saving bumps the version, which makes
 * everyone re-take the quiz. */
function TrainingPanel() {
    const [doc, setDoc] = useState(null);
    const [intro, setIntro] = useState('');
    const [videos, setVideos] = useState([]);
    const [quiz, setQuiz] = useState([]);
    const [passingScore, setPassingScore] = useState(0);
    const [minWatchSec, setMinWatchSec] = useState(15);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get('/training-content').then(r => {
            setDoc(r.data);
            setIntro(r.data.introHtml || '');
            setVideos(r.data.videos || []);
            setQuiz(r.data.quiz || []);
            setPassingScore(r.data.passingScore || 0);
            setMinWatchSec(Number.isFinite(Number(r.data.minWatchSec)) ? Number(r.data.minWatchSec) : 15);
        });
    }, []);

    if (!doc) return <Section title="Training"><p className="text-sm text-ink-500">Loading…</p></Section>;

    const save = async () => {
        /* Clean videos and quiz on the client so the version-bump preview
           (toast / VersionPill) matches what the server stored. The server
           also re-cleans — keep this defensive. */
        const cleanedVideos = videos
            .map(v => ({
                ...v,
                title: String(v.title || '').trim(),
                url:   String(v.url   || '').trim(),
                durationSec: Math.max(0, Number(v.durationSec) || 0),
                required:    v.required !== false,
            }))
            .filter(v => v.title && v.url);

        const cleanedQuiz = quiz
            .map(q => {
                const choices = (q.choices || []).map(c => String(c).trim()).filter(Boolean);
                const correctIndex = Math.max(0, Math.min(choices.length - 1, Number(q.correctIndex) || 0));
                return { question: String(q.question || '').trim(), choices, correctIndex };
            })
            .filter(q => q.question && q.choices.length >= 2);

        setBusy(true);
        try {
            const { data } = await api.patch('/training-content', {
                introHtml: intro,
                videos: cleanedVideos,
                quiz: cleanedQuiz,
                passingScore: Math.max(0, Math.min(cleanedQuiz.length, Number(passingScore) || 0)),
                minWatchSec: Math.max(0, Math.min(600, Number(minWatchSec) || 0)),
            });
            setDoc(data);
            setVideos(data.videos || []);
            setQuiz(data.quiz || []);
            setPassingScore(data.passingScore || 0);
            setMinWatchSec(Number(data.minWatchSec) || 0);
            const hidden = data.hasContent === false;
            toast.success(hidden
                ? `Training saved · onboarding step hidden (no videos or quiz)`
                : `Training saved · v${data.version}`);
        } finally { setBusy(false); }
    };

    /* ── Video helpers ── */
    const addVideo = () => setVideos(v => [...v, { title: '', url: '', durationSec: 0, required: true }]);
    const rmVideo  = (i) => setVideos(v => v.filter((_, x) => x !== i));
    const setVideo = (i, patch) => setVideos(v => v.map((x, n) => n === i ? { ...x, ...patch } : x));

    /* ── Quiz helpers ── */
    const addQuestion = () => setQuiz(q => [...q, { question: '', choices: ['', ''], correctIndex: 0 }]);
    const rmQuestion  = (i) => setQuiz(q => q.filter((_, x) => x !== i));
    const setQuestion = (i, patch) => setQuiz(q => q.map((x, n) => n === i ? { ...x, ...patch } : x));
    const addChoice   = (i) => setQuiz(q => q.map((x, n) => n === i ? { ...x, choices: [...x.choices, ''] } : x));
    const rmChoice    = (i, ci) => setQuiz(q => q.map((x, n) => {
        if (n !== i) return x;
        const choices = x.choices.filter((_, c) => c !== ci);
        const correctIndex = Math.max(0, Math.min(choices.length - 1, x.correctIndex - (ci < x.correctIndex ? 1 : 0)));
        return { ...x, choices, correctIndex };
    }));
    const setChoice   = (i, ci, val) => setQuiz(q => q.map((x, n) => n === i
        ? { ...x, choices: x.choices.map((c, k) => k === ci ? val : c) }
        : x));

    return (
        <>
            <Section title={<>Training <VersionPill v={doc.version} updatedAt={doc.updatedAt} /></>}>
                <p className="text-xs text-ink-500">
                    Manage the videos &amp; quiz users see at the <code>training</code> onboarding step.
                    Saving bumps the version when content exists. Leave both lists empty to
                    <b> hide the training step</b> for new signups automatically.
                </p>

                <label className="ag-label mt-3">Intro HTML (shown above the videos)</label>
                <textarea className="ag-input min-h-[120px] font-mono text-xs whitespace-pre" value={intro} onChange={e => setIntro(e.target.value)} />
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold mb-1">Preview</div>
                    <div className="ag-onboard-html ag-card p-4 max-h-40 overflow-y-auto"
                         dangerouslySetInnerHTML={{ __html: intro || '<p style="color:var(--ink-500)">(empty)</p>' }} />
                </div>

                <Field
                    label="Minimum watch time per video (seconds)"
                    type="number"
                    value={minWatchSec}
                    onChange={v => setMinWatchSec(Math.max(0, Math.min(600, Number(v) || 0)))}
                    hint="Users can only click 'Mark as watched' after the player has been open this long. 0 disables the gate. Capped at the video's stated duration so a 30-sec clip never asks for 60 seconds."
                />
            </Section>

            <Section title={<>Videos <span className="ml-2 text-xs font-normal text-ink-500">{videos.length}</span></>}>
                {videos.length === 0 && (
                    <div className="text-xs text-ink-500 bg-ink-50 rounded-lg p-3 border border-ink-200">
                        No videos yet. Paste a YouTube watch URL (e.g. <code>https://www.youtube.com/watch?v=…</code>),
                        a YouTube embed URL, or a direct MP4 link.
                    </div>
                )}
                {videos.map((v, i) => (
                    <div key={i} className="rounded-lg ring-1 ring-ink-200 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-bold text-ink-500 uppercase">Video {i + 1}</span>
                            <button type="button" onClick={() => rmVideo(i)} className="text-brand-red hover:opacity-80">
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                        <Field label="Title" value={v.title} onChange={val => setVideo(i, { title: val })} />
                        <Field label="URL (YouTube watch / embed / MP4)" value={v.url} onChange={val => setVideo(i, { url: val })} />
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Duration (sec)" type="number" value={v.durationSec} onChange={val => setVideo(i, { durationSec: Number(val) || 0 })} hint="Used for the dwell-time cap and the trainee's 'X min' label." />
                            <Toggle label="Required to pass" value={!!v.required} onChange={val => setVideo(i, { required: val })} />
                        </div>
                    </div>
                ))}
                <button type="button" onClick={addVideo} className="ag-btn-ghost"><Plus className="h-4 w-4" /> Add video</button>
            </Section>

            <Section title={<>Quiz <span className="ml-2 text-xs font-normal text-ink-500">{quiz.length} question{quiz.length === 1 ? '' : 's'}</span></>}>
                {videos.length === 0 && quiz.length === 0 && (
                    <div className="text-xs text-emerald-800 bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                        No videos or quiz — the <b>Training</b> onboarding step is hidden automatically for new signups.
                    </div>
                )}
                {quiz.length === 0 && videos.length > 0 && (
                    <div className="text-xs text-ink-500 bg-ink-50 rounded-lg p-3 border border-ink-200">
                        No quiz questions — signup training can be video-only.
                    </div>
                )}
                <Field
                    label="Passing score (0 = require all correct)"
                    type="number"
                    value={passingScore}
                    onChange={v => setPassingScore(Number(v) || 0)}
                    hint={`0–${quiz.length}. Higher = stricter.`}
                />
                {quiz.map((q, i) => (
                    <div key={i} className="rounded-lg ring-1 ring-ink-200 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-bold text-ink-500 uppercase">Question {i + 1}</span>
                            <button type="button" onClick={() => rmQuestion(i)} className="text-brand-red hover:opacity-80">
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                        <Field label="Question" value={q.question} onChange={v => setQuestion(i, { question: v })} />
                        <label className="ag-label">Choices (radio = correct answer)</label>
                        {q.choices.map((c, ci) => (
                            <div key={ci} className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    name={`correct-${i}`}
                                    checked={q.correctIndex === ci}
                                    onChange={() => setQuestion(i, { correctIndex: ci })}
                                    className="h-4 w-4 accent-brand-teal shrink-0"
                                />
                                <input
                                    className="ag-input flex-1"
                                    value={c}
                                    placeholder={`Choice ${ci + 1}`}
                                    onChange={e => setChoice(i, ci, e.target.value)}
                                />
                                {q.choices.length > 2 && (
                                    <button type="button" onClick={() => rmChoice(i, ci)} className="text-ink-500 hover:text-brand-red shrink-0">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        ))}
                        <button type="button" onClick={() => addChoice(i)} className="ag-btn-ghost text-xs"><Plus className="h-3.5 w-3.5" /> Add choice</button>
                    </div>
                ))}
                <button type="button" onClick={addQuestion} className="ag-btn-ghost"><Plus className="h-4 w-4" /> Add question</button>
                <SaveBar busy={busy} onSave={save} />
            </Section>
        </>
    );
}

function VersionPill({ v, updatedAt }) {
    return (
        <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded-full bg-brand-teal-50 text-brand-teal-700 px-2 py-0.5 align-middle">
            v{v}{updatedAt && <span className="font-normal text-ink-500 normal-case">· {new Date(updatedAt).toLocaleDateString()}</span>}
        </span>
    );
}

/* ──────────────── primitives ──────────────── */
function Section({ title, children }) {
    return (
        <div className="ag-card p-5 space-y-3">
            <h3 className="font-bold">{title}</h3>
            {children}
        </div>
    );
}
function Field({ label, value, onChange, type='text', hint, placeholder }) {
    return (
        <div>
            <label className="ag-label">{label}</label>
            <input className="ag-input" type={type} value={value ?? ''} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
            {hint && <div className="text-[11px] text-ink-500 mt-1" dangerouslySetInnerHTML={{ __html: hint }} />}
        </div>
    );
}
function Select({ label, value, options, onChange }) {
    return (
        <div>
            <label className="ag-label">{label}</label>
            <select className="ag-input" value={value} onChange={e => onChange(e.target.value)}>
                {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}
function ColorField({ label, value, onChange }) {
    return (
        <div>
            <label className="ag-label">{label}</label>
            <div className="flex items-center gap-2">
                <input type="color" className="h-9 w-12 rounded border border-ink-300 cursor-pointer" value={value} onChange={e => onChange(e.target.value)} />
                <input className="ag-input flex-1 font-mono text-xs" value={value} onChange={e => onChange(e.target.value)} />
            </div>
        </div>
    );
}
function Toggle({ label, value, onChange, hint }) {
    return (
        <label className="flex items-start gap-3 py-1.5 cursor-pointer">
            <span className={`mt-0.5 w-9 h-5 rounded-full transition relative ${value ? 'bg-brand-teal' : 'bg-ink-300'}`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition ${value ? 'left-[18px]' : 'left-0.5'}`} />
                <input type="checkbox" className="sr-only" checked={value} onChange={e => onChange(e.target.checked)} />
            </span>
            <span className="flex-1">
                <span className="text-sm font-medium">{label}</span>
                {hint && <span className="block text-[11px] text-ink-500 mt-0.5">{hint}</span>}
            </span>
        </label>
    );
}
function SaveBar({ busy, onSave }) {
    return (
        <div className="flex justify-end pt-2">
            <button className="ag-btn-primary" disabled={busy} onClick={onSave}>
                <Save className="h-4 w-4" /> {busy ? 'Saving…' : 'Save changes'}
            </button>
        </div>
    );
}

/* ─── KPI Targets (H2) ───────────────────────────────────────────────────
   Per-role defaults and per-user overrides for every KPI from the PDF, with
   a daily / weekly / monthly cadence selector. Universal benchmarks act as
   the floor — if no override exists, the row shows the PDF benchmark with
   a "universal" badge.

   The model has a hard rule (partial unique index): at most one ACTIVE doc
   per (scope, role|user, kpi, cadence). When admin saves a new value we POST
   to /kpi-targets which deactivates the old slot and inserts a fresh one. */
function KpiTargetsPanel() {
    const [meta, setMeta] = useState(null);
    const [scope, setScope] = useState('role');             // 'role' | 'user'
    const [role, setRole]   = useState('bda');
    const [users, setUsers] = useState([]);
    const [userId, setUserId] = useState('');
    const [cadence, setCadence] = useState('monthly');
    const [items, setItems] = useState([]);                  // resolved effective rows
    const [edits, setEdits] = useState({});                  // kpi → new value buffer
    const [busy, setBusy]   = useState(false);
    const [seeding, setSeeding] = useState(false);

    /* Load metadata + (for the user picker) the user directory. */
    useEffect(() => { api.get('/kpi-targets/metadata').then(r => setMeta(r.data)); }, []);
    useEffect(() => {
        if (scope !== 'user') return;
        api.get('/users', { params: { status: 'active' } }).then(r => {
            const sales = (r.data.items || []).filter(u =>
                ['bda', 'bde', 'bdm', 'performance_marketing'].includes(u.salesDesignation));
            setUsers(sales);
            if (!userId && sales[0]) setUserId(sales[0]._id);
        });
    }, [scope]);

    /* Re-fetch effective rows whenever scope / role / user / cadence change. */
    useEffect(() => {
        if (!meta) return;
        const params = { cadence };
        if (scope === 'role') params.role = role;
        if (scope === 'user' && userId) params.user = userId;
        else if (scope === 'user' && !userId) { setItems([]); return; }
        setBusy(true);
        api.get('/kpi-targets/effective', { params })
            .then(r => { setItems(r.data.items || []); setEdits({}); })
            .finally(() => setBusy(false));
    }, [meta, scope, role, userId, cadence]);

    const dirty = Object.keys(edits).length;
    const setBuffered = (kpi, v) => setEdits(e => ({ ...e, [kpi]: v }));
    const discard = () => setEdits({});

    const saveAll = async () => {
        if (!dirty) return;
        setBusy(true);
        try {
            const targetUser = scope === 'user' ? userId : null;
            await Promise.all(Object.entries(edits).map(([kpi, raw]) => {
                const value = Number(raw);
                if (Number.isNaN(value) || value < 0) return null;
                const row = items.find(i => i.kpi === kpi) || {};
                const payload = {
                    scope, kpi, cadence,
                    targetValue: value,
                    unit: row.unit || 'count',
                    note: 'Admin override',
                };
                if (scope === 'role') payload.role = role;
                else payload.user = targetUser;
                return api.post('/kpi-targets', payload);
            }).filter(Boolean));
            toast.success(`Saved ${dirty} target${dirty === 1 ? '' : 's'}`);
            /* refetch */
            const params = { cadence };
            if (scope === 'role') params.role = role;
            else params.user = targetUser;
            const r = await api.get('/kpi-targets/effective', { params });
            setItems(r.data.items || []);
            setEdits({});
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Save failed');
        } finally { setBusy(false); }
    };

    const resetRow = async (row) => {
        if (row.source === 'universal' || row.source === 'unset') return;
        if (!confirm(`Reset ${row.label} to the universal benchmark? This deactivates the current ${row.source} override.`)) return;
        try {
            await api.post(`/kpi-targets/${row._id}/deactivate`);
            toast.success(`${row.label} reset`);
            const params = { cadence };
            if (scope === 'role') params.role = role; else params.user = userId;
            const r = await api.get('/kpi-targets/effective', { params });
            setItems(r.data.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Reset failed');
        }
    };

    const seedDefaults = async () => {
        if (!confirm('Seed every role × KPI × cadence with the PDF universal benchmark? Skips any slots that already have a target. Safe to re-run.')) return;
        setSeeding(true);
        try {
            const { data } = await api.post('/kpi-targets/seed-defaults');
            toast.success(`Seeded ${data.created} default${data.created === 1 ? '' : 's'}, skipped ${data.skipped}`);
            const params = { cadence };
            if (scope === 'role') params.role = role; else if (userId) params.user = userId;
            const r = await api.get('/kpi-targets/effective', { params });
            setItems(r.data.items || []);
        } finally { setSeeding(false); }
    };

    if (!meta) return <div className="ag-card p-6 text-sm text-ink-500">Loading…</div>;

    const sourceChip = (src) => {
        const map = {
            user:      { txt: 'User override',  cls: 'bg-brand-teal-50 text-brand-teal-700' },
            role:      { txt: 'Role default',   cls: 'bg-amber-50 text-amber-700' },
            universal: { txt: 'PDF benchmark',  cls: 'bg-ink-100 text-ink-600' },
            unset:     { txt: 'Unset',          cls: 'bg-rose-50 text-rose-700' },
        };
        const m = map[src] || map.unset;
        return <span className={`ag-chip ${m.cls}`}>{m.txt}</span>;
    };

    const fmtUnit = (unit) => unit === 'percent' ? '%' : unit === 'rupees' ? '₹' : '';
    const selectedUser = users.find(u => u._id === userId);

    return (
        <div className="space-y-4">
            <div className="ag-card p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="text-lg font-extrabold flex items-center gap-2">
                            <Target className="h-5 w-5 text-brand-teal-700" /> KPI Targets
                        </h2>
                        <p className="text-sm text-ink-500 mt-1 max-w-2xl">
                            Set per-role defaults (apply to everyone with that designation) or per-user overrides (win over the default).
                            Counts scale automatically across cadences — a monthly target of 100 becomes ~25 weekly and ~5 daily.
                            Percentages stay constant. Falls back to the PDF benchmark when nothing is set.
                        </p>
                    </div>
                    <button className="ag-btn-ghost text-xs" disabled={seeding} onClick={seedDefaults}>
                        <Sparkles className="h-4 w-4" />{seeding ? 'Seeding…' : 'Seed PDF defaults'}
                    </button>
                </div>
            </div>

            <div className="ag-card p-4 flex flex-wrap gap-3 items-end">
                <div>
                    <label className="ag-label">Scope</label>
                    <div className="inline-flex rounded-lg border border-ink-200 overflow-hidden">
                        {['role', 'user'].map(opt => (
                            <button key={opt} onClick={() => setScope(opt)}
                                className={`px-3 py-1.5 text-xs font-semibold ${scope === opt ? 'bg-brand-teal-600 text-white' : 'bg-white text-ink-700 hover:bg-ink-50'}`}>
                                {opt === 'role' ? 'Role default' : 'User override'}
                            </button>
                        ))}
                    </div>
                </div>
                {scope === 'role' && (
                    <div>
                        <label className="ag-label">Role</label>
                        <select className="ag-input" value={role} onChange={e => setRole(e.target.value)}>
                            {meta.roles.map(r => (
                                <option key={r} value={r}>
                                    {r === 'performance_marketing' ? 'Performance Marketing' : r.toUpperCase()}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                {scope === 'user' && (
                    <div className="min-w-[260px]">
                        <label className="ag-label">User</label>
                        <select className="ag-input" value={userId} onChange={e => setUserId(e.target.value)}>
                            <option value="">— pick a user —</option>
                            {users.map(u => (
                                <option key={u._id} value={u._id}>
                                    {u.name} · {u.salesDesignation?.toUpperCase()} · {u.officeBranch || '—'}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                <div>
                    <label className="ag-label">Cadence</label>
                    <div className="inline-flex rounded-lg border border-ink-200 overflow-hidden">
                        {meta.cadences.map(c => (
                            <button key={c} onClick={() => setCadence(c)}
                                className={`px-3 py-1.5 text-xs font-semibold ${cadence === c ? 'bg-brand-teal-600 text-white' : 'bg-white text-ink-700 hover:bg-ink-50'}`}>
                                {c[0].toUpperCase() + c.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
                {dirty > 0 && (
                    <div className="ml-auto flex items-center gap-2">
                        <span className="text-xs text-amber-700 inline-flex items-center gap-1">
                            <AlertTriangle className="h-3.5 w-3.5" /> {dirty} unsaved
                        </span>
                        <button className="ag-btn-ghost text-xs" onClick={discard}>Discard</button>
                        <button className="ag-btn-primary text-xs" disabled={busy} onClick={saveAll}>
                            <Save className="h-4 w-4" />{busy ? 'Saving…' : `Save ${dirty}`}
                        </button>
                    </div>
                )}
            </div>

            <div className="ag-card p-0 overflow-x-auto">
                <table className="ag-table">
                    <thead><tr>
                        <th>KPI</th>
                        <th>Current target ({cadence})</th>
                        <th>Source</th>
                        <th>New value</th>
                        <th></th>
                    </tr></thead>
                    <tbody>
                        {items.map(row => {
                            const buffered = edits[row.kpi];
                            const displayValue = row.value == null ? '—' : `${row.value}${fmtUnit(row.unit)}`;
                            return (
                                <tr key={row.kpi}>
                                    <td className="font-medium">
                                        {row.label}
                                        <div className="text-[11px] text-ink-500">{row.kpi}</div>
                                    </td>
                                    <td className="font-mono text-sm">{displayValue}</td>
                                    <td>{sourceChip(row.source)}</td>
                                    <td>
                                        <div className="inline-flex items-center gap-1">
                                            <input
                                                type="number"
                                                min="0"
                                                step={row.unit === 'percent' ? 1 : 1}
                                                className="ag-input text-xs py-1 w-24"
                                                placeholder={row.value == null ? '—' : String(row.value)}
                                                value={buffered ?? ''}
                                                onChange={e => setBuffered(row.kpi, e.target.value)}
                                            />
                                            <span className="text-xs text-ink-500">{fmtUnit(row.unit)}</span>
                                        </div>
                                    </td>
                                    <td className="text-right">
                                        {(row.source === 'role' && scope === 'role')
                                          || (row.source === 'user' && scope === 'user') ? (
                                            <button className="ag-btn-ghost text-xs" onClick={() => resetRow(row)}>
                                                <Trash2 className="h-3.5 w-3.5" /> Reset
                                            </button>
                                        ) : null}
                                    </td>
                                </tr>
                            );
                        })}
                        {items.length === 0 && (
                            <tr><td colSpan={5} className="text-center text-ink-500 py-8">
                                {scope === 'user' && !userId
                                    ? 'Pick a user to view their targets.'
                                    : 'No KPIs applicable for this scope.'}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="ag-card p-4 text-xs text-ink-500 space-y-1">
                <div className="flex items-start gap-2">
                    <Pencil className="h-3.5 w-3.5 mt-0.5 text-brand-teal-700" />
                    <div>
                        <b>How resolution works:</b> when the KPI engine evaluates a user, it first looks for
                        a <i>user override</i> for that user × KPI × cadence. If none exists, it falls back to
                        the <i>role default</i> for their designation. If that's also missing, it uses the
                        PDF universal benchmark (100 → 50 → 25 → 10 → 9 → 1, scaled to cadence).
                    </div>
                </div>
                {scope === 'user' && selectedUser && (
                    <div className="text-ink-600 mt-2">
                        Editing overrides for <b>{selectedUser.name}</b> · {selectedUser.salesDesignation?.toUpperCase()} ·
                        {' '}{selectedUser.officeBranch || '—'}.
                        Anything left at "role" or "PDF benchmark" remains shared with everyone else at this role.
                    </div>
                )}
            </div>
        </div>
    );
}

/* ─── Org Directory (I3) ─────────────────────────────────────────────────
   Centrally manage three admin-curated lists: Designations, Office branches,
   Email domains. Signup, Users editor, and Profile all read from these.

   System rows (the 5 hardcoded designations + the default email domain) are
   protected — admin can edit the label / sort order / activate-deactivate
   but can't rename the slug or hard-delete the row.

   The signup page calls a public /auth/directory snapshot that reflects
   whatever's active here. */
function OrgDirectoryPanel() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = () => api.get('/org-directory').then(r => setData(r.data));
    useEffect(() => { load(); }, []);

    const create = async (type, payload) => {
        setBusy(true);
        try {
            await api.post('/org-directory', { type, ...payload });
            toast.success('Added');
            await load();
        } catch (e) { toast.error(e?.response?.data?.error || 'Add failed'); }
        finally { setBusy(false); }
    };
    const update = async (id, patch) => {
        setBusy(true);
        try {
            await api.patch(`/org-directory/${id}`, patch);
            await load();
        } catch (e) { toast.error(e?.response?.data?.error || 'Update failed'); }
        finally { setBusy(false); }
    };
    const remove = async (id, label) => {
        if (!confirm(`Delete "${label}"? This can't be undone.`)) return;
        setBusy(true);
        try {
            await api.delete(`/org-directory/${id}`);
            toast.success('Deleted');
            await load();
        } catch (e) { toast.error(e?.response?.data?.error || 'Delete failed'); }
        finally { setBusy(false); }
    };

    if (!data) return <div className="ag-card p-6 text-sm text-ink-500">Loading…</div>;

    const sections = [
        {
            type: 'designation',
            title: 'Designations',
            subtitle: 'Job titles shown in signup, Users editor & profile. BDA/BDE/BDM/Operations/Performance Marketing are system slugs the KPI engine + incentive logic look up by name — don\'t rename them.',
            icon: Briefcase,
            tone: 'bg-sky-50 border-sky-200 text-sky-900',
            placeholder: 'e.g. Senior BDA · Telecaller',
            slugHint: 'Lowercase, no spaces. e.g. senior_bda',
        },
        {
            type: 'office',
            title: 'Office branches',
            subtitle: 'Locations shown in signup, Users, and the client form. Admin-only — sales users can\'t add new branches at signup.',
            icon: MapPin,
            tone: 'bg-amber-50 border-amber-200 text-amber-900',
            placeholder: 'e.g. Mumbai',
            slugHint: 'Lowercase identifier. e.g. mumbai',
        },
        {
            type: 'email_domain',
            title: 'Allowed email domains',
            subtitle: 'Restrict signup to these @domains. Leave empty to allow any email (legacy behaviour).',
            icon: AtSign,
            tone: 'bg-emerald-50 border-emerald-200 text-emerald-900',
            placeholder: 'e.g. ageon.in',
            slugHint: 'Just the domain — no leading @. e.g. ageonrealty.in',
        },
    ];

    return (
        <div className="space-y-4">
            <div className="ag-card p-5">
                <h2 className="text-lg font-extrabold flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-brand-teal-700" /> Org Directory
                </h2>
                <p className="text-sm text-ink-500 mt-1 max-w-3xl">
                    The single source of truth for designations, office branches, and allowed signup-email domains.
                    The signup page, Users editor, and user profile all read from these lists. System rows are
                    locked from renaming/deletion but can be deactivated to hide them from dropdowns.
                </p>
            </div>

            {sections.map(s => (
                <DirectorySection
                    key={s.type}
                    section={s}
                    items={(data.grouped?.[s.type]) || []}
                    busy={busy}
                    onCreate={p => create(s.type, p)}
                    onUpdate={update}
                    onDelete={remove}
                />
            ))}
        </div>
    );
}

function DirectorySection({ section, items, busy, onCreate, onUpdate, onDelete }) {
    const [draft, setDraft] = useState({ slug: '', label: '', sortOrder: 100 });
    const reset = () => setDraft({ slug: '', label: '', sortOrder: 100 });
    const add = async (e) => {
        e?.preventDefault();
        if (!draft.label.trim()) return toast.error('Label required');
        await onCreate({ slug: draft.slug.trim() || draft.label.trim(), label: draft.label.trim(), sortOrder: Number(draft.sortOrder) || 100 });
        reset();
    };
    const Icon = section.icon;
    return (
        <div className="ag-card overflow-hidden">
            <div className={`px-4 py-3 border-b flex items-center gap-2 ${section.tone}`}>
                <Icon className="h-4 w-4" />
                <div className="flex-1">
                    <div className="font-bold text-sm">{section.title}</div>
                    <div className="text-[11px] opacity-80">{section.subtitle}</div>
                </div>
                <span className="ag-chip bg-white/70 text-current border border-current/20 text-[10px] font-bold">
                    {items.filter(i => i.active).length} active · {items.length} total
                </span>
            </div>

            {/* Add form */}
            <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_90px_auto] gap-2 p-4 border-b border-ink-100 items-end">
                <div>
                    <label className="ag-label text-[10px]">Display label</label>
                    <input className="ag-input text-sm" placeholder={section.placeholder}
                        value={draft.label}
                        onChange={e => setDraft({ ...draft, label: e.target.value })} />
                </div>
                <div>
                    <label className="ag-label text-[10px]">Slug</label>
                    <input className="ag-input text-sm" placeholder={section.slugHint}
                        value={draft.slug}
                        onChange={e => setDraft({ ...draft, slug: e.target.value })} />
                </div>
                <div>
                    <label className="ag-label text-[10px]">Sort</label>
                    <input type="number" className="ag-input text-sm" value={draft.sortOrder}
                        onChange={e => setDraft({ ...draft, sortOrder: e.target.value })} />
                </div>
                <button className="ag-btn-primary text-xs" disabled={busy}>
                    <Plus className="h-4 w-4" /> Add
                </button>
            </form>

            {/* Items */}
            <div className="overflow-x-auto">
                <table className="ag-table">
                    <thead><tr>
                        <th>Label</th><th>Slug</th><th>Sort</th><th>Active</th><th></th>
                    </tr></thead>
                    <tbody>
                        {items.map(it => (
                            <DirectoryRow key={it._id} item={it} busy={busy} onUpdate={onUpdate} onDelete={onDelete} />
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={5} className="text-center text-ink-500 py-8 text-sm">No entries yet — add the first one above.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function DirectoryRow({ item, busy, onUpdate, onDelete }) {
    const [label, setLabel] = useState(item.label);
    const [slug, setSlug]   = useState(item.slug);
    const [sortOrder, setSortOrder] = useState(item.sortOrder ?? 100);
    const dirty = (
        label !== item.label
        || (!item.isSystem && slug !== item.slug)
        || Number(sortOrder) !== (item.sortOrder ?? 100)
    );

    return (
        <tr className={dirty ? 'bg-amber-50/40' : ''}>
            <td>
                <input className="ag-input text-xs py-1" value={label} onChange={e => setLabel(e.target.value)} />
                {item.isSystem && (
                    <span className="inline-flex items-center gap-1 mt-1 text-[10px] text-ink-500">
                        <Lock className="h-3 w-3" /> System
                    </span>
                )}
            </td>
            <td>
                {item.isSystem ? (
                    <span className="font-mono text-[11px] text-ink-500">{item.slug}</span>
                ) : (
                    <input className="ag-input text-xs py-1 font-mono" value={slug} onChange={e => setSlug(e.target.value)} />
                )}
            </td>
            <td>
                <input type="number" className="ag-input text-xs py-1 w-20" value={sortOrder} onChange={e => setSortOrder(e.target.value)} />
            </td>
            <td>
                <label className="inline-flex items-center gap-1 cursor-pointer text-xs">
                    <input type="checkbox" checked={item.active} onChange={e => onUpdate(item._id, { active: e.target.checked })} />
                    {item.active ? 'Active' : 'Hidden'}
                </label>
            </td>
            <td className="text-right">
                <div className="flex gap-1 justify-end">
                    {dirty && (
                        <button className="ag-btn-primary text-xs" disabled={busy} onClick={() => onUpdate(item._id, {
                            label, sortOrder: Number(sortOrder),
                            ...(item.isSystem ? {} : { slug }),
                        })}>
                            <Save className="h-3.5 w-3.5" /> Save
                        </button>
                    )}
                    {!item.isSystem && (
                        <button className="ag-btn-danger text-xs" disabled={busy} onClick={() => onDelete(item._id, item.label)}>
                            <Trash2 className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );
}

/* ─── KPI Report Panel ─── */
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: `${String(i).padStart(2, '0')}:00 IST (${i === 0 ? '12AM' : i < 12 ? `${i}AM` : i === 12 ? '12PM' : `${i - 12}PM`})`,
}));

function KpiReportPanel({ s, busy, onSave }) {
    const kpiInit = s.kpi || {};
    const drInit  = kpiInit.dailyReport || {};
    const [leadDayStartHour, setLeadDayStartHour] = useState(kpiInit.leadDayStartHour ?? 18);
    const [enabled, setEnabled]     = useState(drInit.enabled ?? false);
    const [sendHour, setSendHour]   = useState(drInit.sendHour ?? 19);
    const [recipients, setRecipients] = useState(drInit.recipientUserIds || []);
    const [users, setUsers]         = useState([]);
    const [userSearch, setUserSearch] = useState('');

    useEffect(() => {
        // Load admin users and bhead (Business Head) users for recipient picker
        Promise.all([
            api.get('/users?role=admin&status=active&limit=200'),
            api.get('/users?salesDesignation=bhead&status=active&limit=200'),
        ])
            .then(([admins, bheads]) => {
                const all = [...(admins.data?.users || admins.data || []), ...(bheads.data?.users || bheads.data || [])];
                const seen = new Set();
                setUsers(all.filter(u => { const k = String(u._id); if (seen.has(k)) return false; seen.add(k); return true; }));
            })
            .catch(() => {});
    }, []);

    const filtered = users.filter(u =>
        !recipients.includes(String(u._id)) &&
        (u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
         u.email?.toLowerCase().includes(userSearch.toLowerCase()))
    );

    const addRecipient = (id) => setRecipients(p => [...p, id]);
    const removeRecipient = (id) => setRecipients(p => p.filter(x => x !== id));

    const recipientUsers = users.filter(u => recipients.includes(String(u._id)));

    const save = () => onSave({
        kpi: {
            leadDayStartHour,
            dailyReport: { enabled, sendHour, recipientUserIds: recipients },
        },
    });

    return (
        <div className="space-y-6">
            <Section title="Lead Day Window">
                <p className="text-sm text-ink-500 mb-4">
                    Defines when a new "lead day" starts. Leads created from <strong>yesterday {HOUR_OPTIONS[leadDayStartHour]?.label}</strong> to <strong>today {HOUR_OPTIONS[leadDayStartHour]?.label}</strong> will count as today's leads across all dashboards and reports.
                </p>
                <div className="max-w-xs">
                    <label className="block text-sm font-medium text-ink-700 mb-1">Lead Day Start Time</label>
                    <select
                        value={leadDayStartHour}
                        onChange={e => setLeadDayStartHour(Number(e.target.value))}
                        className="w-full border border-ink-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal"
                    >
                        {HOUR_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                    <p className="text-xs text-ink-400 mt-1">Example: set to 18:00 IST → Jun 4 6PM to Jun 5 6PM = Jun 5 leads</p>
                </div>
            </Section>

            <Section title="Daily KPI Report Email">
                <div className="space-y-5">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setEnabled(p => !p)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-brand-teal' : 'bg-ink-200'}`}
                        >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                        <span className="text-sm font-medium text-ink-700">Enable daily report email</span>
                    </div>

                    {enabled && (
                        <>
                            <div className="max-w-xs">
                                <label className="block text-sm font-medium text-ink-700 mb-1">Send Report At</label>
                                <select
                                    value={sendHour}
                                    onChange={e => setSendHour(Number(e.target.value))}
                                    className="w-full border border-ink-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal"
                                >
                                    {HOUR_OPTIONS.map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-ink-700 mb-2">Report Recipients</label>
                                {recipientUsers.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {recipientUsers.map(u => (
                                            <span key={u._id} className="inline-flex items-center gap-1 bg-brand-teal-50 text-brand-teal-700 text-sm px-3 py-1 rounded-full">
                                                {u.name}
                                                <button type="button" onClick={() => removeRecipient(String(u._id))} className="ml-1 hover:text-red-500">
                                                    <X size={12} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <input
                                    type="text"
                                    placeholder="Search admin / bhead users to add..."
                                    value={userSearch}
                                    onChange={e => setUserSearch(e.target.value)}
                                    className="w-full border border-ink-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal mb-2"
                                />
                                {userSearch && (
                                    <div className="border border-ink-200 rounded-lg max-h-48 overflow-y-auto">
                                        {filtered.length === 0 && (
                                            <div className="px-3 py-2 text-sm text-ink-400">No users found</div>
                                        )}
                                        {filtered.slice(0, 10).map(u => (
                                            <button
                                                key={u._id}
                                                type="button"
                                                onClick={() => { addRecipient(String(u._id)); setUserSearch(''); }}
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-ink-50 border-b border-ink-100 last:border-0"
                                            >
                                                <span className="font-medium">{u.name}</span>
                                                <span className="text-ink-400 ml-2">{u.email}</span>
                                                <span className="text-xs text-ink-300 ml-2">({u.salesDesignation || u.role})</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </Section>

            <SaveBar busy={busy} onSave={save} />
        </div>
    );
}

/* ─── Legal (LOI / Agreement stages) ─── */
function LegalPanel({ s, busy, onSave }) {
    const [subTab, setSubTab] = useState('loi');
    const [loiStages, setLoiStages] = useState(() => sortLegalStages(s.legal?.loiStages));
    const [agreementStages, setAgreementStages] = useState(() => sortLegalStages(s.legal?.agreementStages));
    const [newLabel, setNewLabel] = useState('');

    useEffect(() => {
        setLoiStages(sortLegalStages(s.legal?.loiStages));
        setAgreementStages(sortLegalStages(s.legal?.agreementStages));
    }, [s]);

    const stages = subTab === 'loi' ? loiStages : agreementStages;
    const setStages = subTab === 'loi' ? setLoiStages : setAgreementStages;

    const addStage = () => {
        const label = newLabel.trim();
        if (!label) return toast.error('Enter a stage name');
        const maxOrder = stages.reduce((m, st) => Math.max(m, st.order || 0), 0);
        setStages(prev => [...prev, { order: maxOrder + 1, label, active: true }]);
        setNewLabel('');
    };

    const removeStage = (order) => {
        setStages(prev => prev.filter(st => st.order !== order).map((st, i) => ({ ...st, order: i + 1 })));
    };

    const moveStage = (order, dir) => {
        setStages(prev => {
            const list = sortLegalStages(prev);
            const idx = list.findIndex(st => st.order === order);
            const swap = idx + dir;
            if (swap < 0 || swap >= list.length) return prev;
            [list[idx], list[swap]] = [list[swap], list[idx]];
            return list.map((st, i) => ({ ...st, order: i + 1 }));
        });
    };

    const updateLabel = (order, label) => {
        setStages(prev => prev.map(st => st.order === order ? { ...st, label } : st));
    };

    const toggleActive = (order) => {
        setStages(prev => prev.map(st => st.order === order ? { ...st, active: !st.active } : st));
    };

    const save = () => onSave({
        legal: {
            loiStages: sortLegalStages(loiStages),
            agreementStages: sortLegalStages(agreementStages),
        },
    });

    return (
        <div className="space-y-4">
            <Section title="Legal workflows">
                <p className="text-xs text-ink-500 mb-4">
                    Manage LOI and Agreement stage pipelines. Stages are ordered by number and appear on the client detail page LOI progress tracker.
                </p>

                <div className="inline-flex rounded-xl bg-ink-100 p-1 mb-5">
                    {[
                        { id: 'loi', label: 'LOI' },
                        { id: 'agreement', label: 'Agreement' },
                    ].map(t => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setSubTab(t.id)}
                            className={`rounded-lg px-5 py-2 text-sm font-semibold transition ${
                                subTab === t.id ? 'bg-white text-brand-teal-700 shadow-sm' : 'text-ink-600 hover:text-ink-900'
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="space-y-2">
                    {stages.length === 0 && (
                        <p className="text-sm text-ink-500 py-6 text-center rounded-xl border border-dashed border-ink-200">No stages yet. Add your first stage below.</p>
                    )}
                    {stages.map((st, idx) => (
                        <div
                            key={`${subTab}-${st.order}`}
                            className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${
                                st.active === false ? 'border-ink-100 bg-ink-50/80 opacity-60' : 'border-ink-200 bg-white'
                            }`}
                        >
                            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-teal-50 text-brand-teal-700 font-mono text-sm font-bold ring-1 ring-brand-teal/15">
                                {st.order}
                            </span>
                            <GripVertical className="h-4 w-4 text-ink-300 shrink-0" aria-hidden />
                            <input
                                className="ag-input flex-1 py-1.5 text-sm"
                                value={st.label}
                                onChange={e => updateLabel(st.order, e.target.value)}
                            />
                            <label className="flex items-center gap-1.5 text-xs text-ink-500 shrink-0 cursor-pointer">
                                <input type="checkbox" checked={st.active !== false} onChange={() => toggleActive(st.order)} />
                                Active
                            </label>
                            <div className="flex items-center gap-0.5 shrink-0">
                                <button type="button" className="p-1.5 rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-700 disabled:opacity-30" disabled={idx === 0} onClick={() => moveStage(st.order, -1)} aria-label="Move up">↑</button>
                                <button type="button" className="p-1.5 rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-700 disabled:opacity-30" disabled={idx === stages.length - 1} onClick={() => moveStage(st.order, 1)} aria-label="Move down">↓</button>
                                <button type="button" className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50" onClick={() => removeStage(st.order)} aria-label="Remove">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <input
                        className="ag-input flex-1"
                        value={newLabel}
                        onChange={e => setNewLabel(e.target.value)}
                        placeholder={`New ${subTab === 'loi' ? 'LOI' : 'Agreement'} stage name…`}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addStage(); } }}
                    />
                    <button type="button" className="ag-btn-outline shrink-0" onClick={addStage}>
                        <Plus className="h-4 w-4" /> Add new stage
                    </button>
                </div>
            </Section>
            <SaveBar busy={busy} onSave={save} />
        </div>
    );
}

function sortLegalStages(list) {
    return [...(list || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
}

/* Map AppSetting.theme (named keys teal_700) → applyTheme.tokens shape (named teal_700 too — direct passthrough). */
function mapThemeForApply(theme) {
    return {
        brand: {
            teal: theme.teal, teal_700: theme.teal_700, teal_50: theme.teal_50,
            yellow: theme.yellow, red: theme.red, dark: theme.dark,
            orange: theme.orange, gold: theme.gold, ivory: theme.ivory,
        },
        density: theme.density,
        radius:  theme.radius,
    };
}
